<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
import api from '@/api'

// 访客唯一标识（localStorage 持久化，用于统计独立访客数）
function getVisitorId() {
  let id = localStorage.getItem('visitor_id')
  if (!id) {
    id = 'v-' + Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 9)
    localStorage.setItem('visitor_id', id)
  }
  return id
}

// 同路径 5 秒内不重复上报（防止刷新/重复跳转刷数据）
const lastReported = new Map()

export default {
  name: 'App',
  async created() {
    // Fetch site SEO info once and store globally
    try {
      const res = await api.get('/api/company')
      window.__SITE_DATA = res.data || {}
    } catch (e) {
      window.__SITE_DATA = {}
    }
    // 通知 prerender 插件：数据已加载，可以生成快照
    if (typeof document !== 'undefined' && document.dispatchEvent) {
      document.dispatchEvent(new Event('prerender-ready'))
    }
    // 路由切换时上报访问（统计访问人数/IP/国家/页面/来源）
    this.$router.afterEach((to, from) => {
      // 不统计后台管理页面
      if (to.path.startsWith('/admin')) return
      const now = Date.now()
      const last = lastReported.get(to.path) || 0
      if (now - last < 5000) return
      lastReported.set(to.path, now)
      api.post('/api/access-log', {
        page: to.fullPath,
        referer: from.path && from.path !== to.path ? from.fullPath : '',
        userAgent: navigator.userAgent || '',
        visitorId: getVisitorId()
      }).catch(() => {})
    })
    // 首次进入页面时上报一次（初始导航不触发 afterEach，这里捕获站外来源 document.referrer，
    // 例如从 Google/Bing/Facebook 等外部链接直接进入时记录跳转来源 URL）
    this.$router.isReady().then(() => {
      // 注意：vue-router 4 中 $router.currentRoute 是 ref，必须用 $route（自动解包）取当前路由
      const route = this.$route
      if (route && route.path && route.path.startsWith('/admin')) return
      const now = Date.now()
      lastReported.set(route.path, now)
      api.post('/api/access-log', {
        page: route.fullPath,
        referer: document.referrer || '',
        userAgent: navigator.userAgent || '',
        visitorId: getVisitorId()
      }).catch(() => {})
    })
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');

:root {
  --primary: #1a1a1a;
  --accent: #c8a97e;
  --accent-hover: #b8956a;
  --light-bg: #faf9f7;
  --dark-bg: #1a1a1a;
  --text: #333333;
  --text-light: #888888;
  --border: #e8e5e1;
  --white: #ffffff;
  --danger: #dc3545;
  --success: #198754;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', sans-serif;
  color: var(--text);
  background: var(--white);
  -webkit-font-smoothing: antialiased;
}

h1, h2, h3, h4, .font-serif {
  font-family: 'Playfair Display', serif;
}

a {
  text-decoration: none;
  color: inherit;
}

.btn-gold {
  background: var(--accent);
  color: white;
  border: none;
  padding: 12px 32px;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.btn-gold:hover {
  background: var(--accent-hover);
  color: white;
}

.btn-outline-gold {
  border: 2px solid var(--accent);
  color: var(--accent);
  background: transparent;
  padding: 10px 30px;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.btn-outline-gold:hover {
  background: var(--accent);
  color: white;
}

.section-title {
  font-size: 2rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.5rem;
}

.section-subtitle {
  color: var(--text-light);
  text-align: center;
  margin-bottom: 3rem;
  font-size: 1rem;
}

.page-hero {
  background: linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920') center/cover;
  height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.page-hero h1 {
  font-size: 2.8rem;
  font-weight: 700;
}

/* 移动端：大图横幅文字缩小（与首页 hero 文字一致），靠左距左 20px */
@media (max-width: 767.98px) {
  .page-hero {
    height: 220px;
    justify-content: flex-start;
    padding-left: 20px;
    padding-right: 20px;
  }

  .page-hero h1 {
    font-size: 2rem;
    line-height: 1.2;
  }
}
</style>
